import React from 'react';
import { useTranslation } from 'react-i18next';
import { compact, join } from 'lodash/array';

import Button from '../../../../../../../common/general/Button';
import Icon from '../../../../../../../common/general/Icon';
import { formatPhoneNumber } from '../../../../../../../services/geo/e-164';
import { NS_COMMON, NSC_ADDRESS, NSC_PATIENT, NSPP_PATIENT } from '../../../../../../../services/i18n/namespaces';
import { getPatientFullName } from '../../../../../../../services/patient';
import { usePatientProfile } from '../../PatientProfileContext';

export default function PatientDetailsContactInformation() {
  const { t } = useTranslation();
  const { editPatientInfo, patientInfo, dictionariesMap, userCanEdit } = usePatientProfile();
  const {
    preferredName,
    phonesInfo,
    pronouns,
    primaryLanguage,
    email,
    smsOptIn,
    address,
    interestedInFutureResearch,
    isDeceased
  } = patientInfo || {};

  const isUserNotAbleToContact = isDeceased || phonesInfo?.neverCallAgain;

  const addressFields = [
    address?.addressLine1,
    address?.addressLine2,
    address?.city,
    address?.state,
    address?.zipCode,
    address?.country
  ];
  const formattedAddress = join(compact(addressFields), ', ');

  return (
    <div className="patient-profile-information-content-section">
      <div className="patient-profile-information-header">
        <div className="patient-profile-information-header-title">{t(`${NSPP_PATIENT}:patientContactDetails`)}</div>
        {userCanEdit && (
          <div className="patient-profile-information-header-edit">
            <Button onClick={editPatientInfo} priority="medium" size="h28">
              <Icon>edit</Icon>
              {t('edit')}
            </Button>
          </div>
        )}
      </div>

      {isUserNotAbleToContact && (
        <div className="patient-profile-information-data-row">
          <div className="patient-profile-information-highlighted-field">
            {t(`${NSPP_PATIENT}:info.DO_NOT_CONTACT`)}
          </div>
          {isDeceased && (
            <div className="patient-profile-information-deceased-chip">{t(`${NSC_PATIENT}:deceased`)}</div>
          )}
        </div>
      )}

      <div className="patient-profile-information-data-row">
        <div className="patient-profile-information-label">
          <div aria-label="patient-full-name-label-field" className="patient-profile-information-label-field">
            {`${t('name', { ns: NS_COMMON })}:`}
          </div>
          <div className="patient-profile-information-label-field">{getPatientFullName(patientInfo)}</div>
        </div>

        <div className="patient-profile-information-label">
          <div className="patient-profile-information-label-field">{`${t('preferredName', {
            ns: NSPP_PATIENT
          })}:`}</div>
          <div className="patient-profile-information-label-field">{preferredName || '-'}</div>
        </div>
      </div>

      <div className="patient-profile-information-data-row">
        <div className="patient-profile-information-label">
          <div className="patient-profile-information-label-field">{`${t('primaryPhone', {
            ns: NSC_PATIENT
          })}:`}</div>
          <div className="patient-profile-information-label-field">
            {formatPhoneNumber(phonesInfo?.primary, phonesInfo?.primaryPhoneCode) || '-'}
          </div>
        </div>

        <div className="patient-profile-information-label">
          <div className="patient-profile-information-label-field">{`${t('pronouns', {
            ns: NSPP_PATIENT
          })}:`}</div>
          <div className="patient-profile-information-label-field">
            {pronouns ? t(`${NSC_PATIENT}:pronounMap.${pronouns}`) : '-'}
          </div>
        </div>

        <div className="patient-profile-information-label">
          <div className="patient-profile-information-label-field">{`${t('primaryLanguage', {
            ns: NSPP_PATIENT
          })}:`}</div>
          <div className="patient-profile-information-label-field">
            {primaryLanguage ? t(`${NSC_PATIENT}:languageMap.${primaryLanguage}`) : '-'}
          </div>
        </div>
      </div>

      <div className="patient-profile-information-data-row">
        <div className="patient-profile-information-label">
          <div className="patient-profile-information-label-field">{`${t('email', {
            ns: NS_COMMON
          })}:`}</div>
          <div aria-label="patient-email-value" className="patient-profile-information-label-field">
            {email || '-'}
          </div>
        </div>
      </div>

      <div className="patient-profile-information-data-row">
        <div className="patient-profile-information-label">
          <div className="patient-profile-information-label-field">{`${t('preferredContactMethod', {
            ns: NSPP_PATIENT
          })}:`}</div>
          <div className="patient-profile-information-label-field">
            {phonesInfo?.preferredContactMethod
              ? t(`${NSC_PATIENT}:contactMethodMap.${phonesInfo?.preferredContactMethod}`)
              : '-'}
          </div>
        </div>

        <div className="patient-profile-information-label">
          <div className="patient-profile-information-label-field">{`${t('info.smsOptIn', {
            ns: NSPP_PATIENT
          })}:`}</div>
          <div className="patient-profile-information-label-field">{resolveDropDownOption(smsOptIn)}</div>
        </div>
      </div>

      <div className="patient-profile-information-data-row">
        <div className="patient-profile-information-label">
          <div className="patient-profile-information-label-field">{`${t('address', {
            ns: NSC_ADDRESS
          })}:`}</div>
          <div className="patient-profile-information-label-field">{formattedAddress}</div>
        </div>
      </div>

      <div className="patient-profile-information-data-row">
        <div className="patient-profile-information-label">
          <div className="patient-profile-information-label-field">{`${t('interestedInFutureResearch', {
            ns: NSPP_PATIENT
          })}:`}</div>
          <div className="patient-profile-information-label-field">
            {resolveDropDownOption(interestedInFutureResearch)}
          </div>
        </div>
      </div>
    </div>
  );

  function resolveDropDownOption(option) {
    if (option === 'YES') {
      return t('Y');
    }
    if (option === 'NO') {
      return t('N');
    }
    return t('notAsked');
  }
}
